import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { create } from 'zustand'
import { fetcher } from 'api/swr'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import { columns } from './data-table-column'
import DataTableToolbar from './data-table-toolbar'

const PAGE_SIZE = 60

const parseIds = (value) =>
  [
    ...new Set(
      String(value || '')
        .split(/[\s,，;；]+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    ),
  ]

const fetchFramesByIds = ([, ids]) =>
  fetcher(['/frames/batch', { ids: ids.join(',') }])

// 创建 Context 来传递 mutate 函数
const MutateContext = createContext()

export const useMutate = () => {
  const mutate = useContext(MutateContext)
  if (!mutate) {
    console.warn('useMutate must be used within a MutateProvider')
    return () => {}
  }
  return mutate
}

export const useForm = create((set) => ({
  order: 'updateTime',
  lang: null,
  langOnly: false,
  after: null,
  afters: [],
  level: null,
  id: '',
  author: '',
  contentTag: null,
  sortByPageReCreateCount: false,
  setLevel: (level) =>
    set({ level: level || null, after: null, afters: [] }),
  setLang: (lang) =>
    set((state) => ({
      lang: lang || null,
      langOnly: lang ? state.langOnly : false,
      after: null,
      afters: [],
    })),
  setLangOnly: (langOnly) =>
    set({ langOnly, after: null, afters: [] }),
  setOrder: (order) => set({ order, after: null, afters: [] }),
  setId: (id) => set({ id, after: null, afters: [] }),
  setAuthor: (author) =>
    set({ author: author || '', after: null, afters: [] }),
  setContentTag: (contentTag) =>
    set({
      contentTag: contentTag || null,
      order: 'updateTime',
      after: null,
      afters: [],
    }),
  setSortByPageReCreateCount: (sortByPageReCreateCount) =>
    set({ sortByPageReCreateCount }),
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null }
      }
      const index = state.afters.findIndex((a) => a === nextAfter)
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] }
      } else {
        return { after: nextAfter }
      }
    }),
}))

const Onlines = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const setContentTag = useForm((s) => s.setContentTag)
  const setAuthor = useForm((s) => s.setAuthor)

  // 支持从 URL 带入筛选，例如 /admin?tplTab=onlines&contentTag=Cat&author=xxx
  useEffect(() => {
    const tag = searchParams.get('contentTag')
    setContentTag(tag || null)
  }, [searchParams, setContentTag])

  useEffect(() => {
    const author = searchParams.get('author')
    setAuthor(author || '')
  }, [searchParams, setAuthor])

  const filterByAuthor = (authorId) => {
    const next = authorId || ''
    setAuthor(next)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next) params.set('author', next)
        else params.delete('author')
        return params
      },
      { replace: true },
    )
  }

  const idInput = useForm((s) => s.id)
  const ids = useMemo(() => parseIds(idInput), [idInput])
  const isMultiIdQuery = ids.length > 1

  const params = useForm((s) => {
    const { order, after, lang, langOnly, level, id, author, contentTag } = s
    const parsedIds = parseIds(id)
    return {
      order,
      after,
      id: parsedIds.length === 1 ? parsedIds[0] : '',
      contentTag,
      limit: PAGE_SIZE,
      // 未选时不传：与 lang=0 / level=0 一样，后端不过滤
      ...(lang > 0 ? { lang } : {}),
      ...(level ? { level } : {}),
      ...(langOnly && lang > 0 ? { langOnly: true } : {}),
      ...(author?.trim() ? { author: author.trim() } : {}),
    }
  })
  const sortByPageReCreateCount = useForm((s) => s.sortByPageReCreateCount)
  const listQuery = useSWR(isMultiIdQuery ? null : ['/frames', params])
  const multiIdQuery = useSWR(
    isMultiIdQuery ? ['/frames/by-ids', ids] : null,
    fetchFramesByIds,
  )
  const { data, isLoading, isValidating, mutate } = isMultiIdQuery
    ? multiIdQuery
    : listQuery
  const tableData = useMemo(() => {
    if (!data) {
      return []
    }
    if (!sortByPageReCreateCount) {
      return data
    }
    return [...data].sort(
      (a, b) => (b.reCreateCount ?? 0) - (a.reCreateCount ?? 0),
    )
  }, [data, sortByPageReCreateCount])
  // 后端 list 后还会 filterOnlineFrames / ES 漏档，实际条数经常 < PAGE_SIZE，
  // 不能用 length >= PAGE_SIZE 判断 hasMore，否则 Next 会直接消失。
  const nextAfter = data?.[data.length - 1]?.id

  const table = useReactTable({
    data: tableData,
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      onPainterClick: (authorId) => {
        if (authorId) filterByAuthor(authorId)
      },
    },
  })

  if (isLoading || isValidating) return <Loader>Load onlines</Loader>
  if (!data) return <EmptyDatas />
  return (
    <MutateContext.Provider value={mutate}>
      <div className="flex flex-col h-[calc(100vh-150px)]">
        <div className="flex-shrink-0 mb-2">
          <DataTableToolbar mutate={mutate} nextAfter={nextAfter} />
        </div>
        <div className="flex-1 min-h-0">
          <Card className="h-full flex flex-col">
            <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
              <div className="h-full overflow-auto">
                <Table table={table} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MutateContext.Provider>
  )
}

export default Onlines
